import React from 'react';

// Product Card Skeleton
export const ProductCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse" role="status" aria-label="Loading product">
    <div className="skeleton h-48 bg-gray-200"></div>
    <div className="p-4 space-y-3">
      <div className="skeleton h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="skeleton h-3 bg-gray-200 rounded w-1/2"></div>
      <div className="flex justify-between items-center">
        <div className="skeleton h-5 bg-gray-200 rounded w-1/3"></div>
        <div className="skeleton h-8 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
  </div>
);

// Product Grid Skeleton
export const ProductGridSkeleton = ({ count = 8 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {Array.from({ length: count }, (_, i) => (
      <ProductCardSkeleton key={i} />
    ))}
  </div>
);

// Cart Item Skeleton
export const CartItemSkeleton = () => (
  <div className="flex items-center space-x-4 p-4 border-b animate-pulse" role="status" aria-label="Loading cart item">
    <div className="skeleton w-16 h-16 bg-gray-200 rounded"></div>
    <div className="flex-1 space-y-2">
      <div className="skeleton h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="skeleton h-3 bg-gray-200 rounded w-1/2"></div>
      <div className="flex justify-between items-center">
        <div className="skeleton h-3 bg-gray-200 rounded w-1/4"></div>
        <div className="skeleton h-6 bg-gray-200 rounded w-16"></div>
      </div>
    </div>
  </div>
);

// Runner/Agent Selector Skeleton
export const RunnerSkeleton = () => (
  <div className="animate-pulse" role="status" aria-label="Loading runners">
    <div className="skeleton h-10 bg-gray-200 rounded mb-4"></div>
    <div className="skeleton h-20 bg-gray-200 rounded"></div>
  </div>
);

// Analytics Card Skeleton
export const AnalyticsCardSkeleton = () => (
  <div className="bg-white p-6 rounded-lg shadow animate-pulse" role="status" aria-label="Loading analytics">
    <div className="flex items-center justify-between mb-4">
      <div className="skeleton h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="skeleton w-8 h-8 bg-gray-200 rounded"></div>
    </div>
    <div className="skeleton h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
    <div className="skeleton h-3 bg-gray-200 rounded w-2/3"></div>
  </div>
);

// Table Row Skeleton
export const TableRowSkeleton = ({ columns = 4 }) => (
  <tr className="animate-pulse" role="status" aria-label="Loading table data">
    {Array.from({ length: columns }, (_, i) => (
      <td key={i} className="px-6 py-4">
        <div className="skeleton h-4 bg-gray-200 rounded"></div>
      </td>
    ))}
  </tr>
);

// Page Loading Skeleton
export const PageLoadingSkeleton = () => (
  <div className="min-h-screen bg-gray-50 animate-pulse" role="status" aria-label="Loading page">
    {/* Header skeleton */}
    <div className="bg-white shadow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="skeleton h-8 bg-gray-200 rounded w-32"></div>
          <div className="flex space-x-4">
            <div className="skeleton h-8 bg-gray-200 rounded w-20"></div>
            <div className="skeleton h-8 bg-gray-200 rounded w-20"></div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Content skeleton */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="skeleton h-12 bg-gray-200 rounded w-1/2 mb-8"></div>
      <ProductGridSkeleton />
    </div>
  </div>
);

// Error Boundary Component
export const ErrorFallback = ({ error, resetErrorBoundary }) => (
  <div 
    className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
    role="alert"
    aria-live="assertive"
  >
    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
      <p className="text-gray-600 mb-4">
        {error?.message || 'An unexpected error occurred'}
      </p>
      <div className="space-y-3">
        <button
          onClick={resetErrorBoundary}
          className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors focus-ring"
        >
          Try again
        </button>
        <button
          onClick={() => window.location.reload()}
          className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors focus-ring"
        >
          Reload page
        </button>
      </div>
    </div>
  </div>
);

// Loading Spinner Component
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  return (
    <div className={`inline-block ${sizeClasses[size]} ${className}`} role="status" aria-label="Loading">
      <svg className="animate-spin text-orange-600" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

const SkeletonLoaderComponents = {
  ProductCardSkeleton,
  ProductGridSkeleton,
  CartItemSkeleton,
  RunnerSkeleton,
  AnalyticsCardSkeleton,
  TableRowSkeleton,
  PageLoadingSkeleton,
  ErrorFallback,
  LoadingSpinner
};

export default SkeletonLoaderComponents;
